import type { CustomElement } from "./utils.ts";

/**
 *
 */
export const extendElementWithActionAttributes = (Base: CustomElement): CustomElement =>
  class extends Base {
    #observer?: MutationObserver;

    /*                                                                                       */
    /*                                                                                          */
    /*                                                                                       */
    #invalidatedChildren = new WeakSet<Element>();

    connectedCallback(): void {
      super.connectedCallback?.();

      const update = (): void => {
        this.#updateHasActionChild();
        this.#updateHasActionChildChecked();
        this.#updateHasActionChildDisabled();
        this.#updateHasActionChildInvalid();
        this.#updateHasActionChildIndeterminate();
      };

      this.#observer = new MutationObserver(update);
      this.#observer.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          "tabindex",
          "disabled",
          "checked",
          "aria-current",
          "aria-pressed",
          "aria-checked",
          "aria-invalid",
        ],
      });

      update();

      const options = {
        passive: true,
      };
      this.addEventListener("pointerdown", this.#setHasActionChildActive, options);
      document.addEventListener("pointerup", this.#clearHasActionChildActive, options);
      document.addEventListener("pointercancel", this.#clearHasActionChildActive, options);

      /*                                                                       */
      /*                                                                        */
      /*                                                                         */
      /*                                              */
      document.addEventListener("change", this.#updateHasActionChildChecked, options);

      /*                                                                          */
      /*                                                                                 */
      document.addEventListener("invalid", this.#handleInvalid, { ...options, capture: true });
      /*                                                                                 */
      document.addEventListener("input", this.#updateHasActionChildInvalid, options);
    }

    disconnectedCallback(): void {
      super.disconnectedCallback?.();

      this.#observer?.disconnect();
      this.#observer = undefined;

      this.removeEventListener("pointerdown", this.#setHasActionChildActive);
      document.removeEventListener("pointerup", this.#clearHasActionChildActive);
      document.removeEventListener("pointercancel", this.#clearHasActionChildActive);

      document.removeEventListener("change", this.#updateHasActionChildChecked);
      document.removeEventListener("invalid", this.#handleInvalid, true);
      document.removeEventListener("input", this.#updateHasActionChildInvalid);
    }

    #updateHasActionChild = (): void => {
      this.toggleAttribute(
        "has-action-child",
        Array.from(this.children).some(
          (child) =>
            child.slot === "" &&
            child.matches('a[href], button, input, select, textarea, [tabindex="0"]'),
        ),
      );

      this.toggleAttribute(
        "has-action-child-enabled",
        Array.from(this.children).some(
          (child) =>
            child.slot === "" &&
            child.matches(
              'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]:not(:disabled)',
            ),
        ),
      );
    };

    #setHasActionChildActive = (): void => {
      this.toggleAttribute("has-action-child-active", true);
    };

    #clearHasActionChildActive = (): void => {
      this.toggleAttribute("has-action-child-active", false);
    };

    #updateHasActionChildChecked = (): void => {
      this.toggleAttribute(
        "has-action-child-checked",
        Array.from(this.children).some(
          (child) =>
            child.slot === "" &&
            ((child as HTMLInputElement).checked ||
              (child.hasAttribute("aria-current") &&
                child.getAttribute("aria-current") !== "false") ||
              child.getAttribute("aria-pressed") === "true" ||
              child.getAttribute("aria-checked") === "true"),
        ),
      );
    };

    #updateHasActionChildDisabled = (): void => {
      this.toggleAttribute(
        "has-action-child-disabled",
        Array.from(this.children).some(
          (child) => child.slot === "" && (child as HTMLInputElement).disabled,
        ),
      );
    };

    #updateHasActionChildInvalid = (): void => {
      this.toggleAttribute(
        "has-action-child-invalid",
        Array.from(this.children).some(
          (child) =>
            child.slot === "" &&
            (child.getAttribute("aria-invalid") === "true" ||
              (this.#invalidatedChildren.has(child) && child.matches(":invalid"))),
        ),
      );
    };

    /*                                                                                 */
    /*                                                                  */
    #handleInvalid = (event: Event): void => {
      const target = event.target as Element | null;
      if (target && target.slot === "" && this.contains(target)) {
        this.#invalidatedChildren.add(target);
      }
      this.#updateHasActionChildInvalid();
    };

    /*                                                                                       */
    /*                                                                                   */
    /*                                                                         */
    #updateHasActionChildIndeterminate = (): void => {
      this.toggleAttribute(
        "has-action-child-indeterminate",
        Array.from(this.children).some(
          (child) => child.slot === "" && (child as HTMLInputElement).indeterminate,
        ),
      );
    };
  };
